import React, { createContext, useContext, useState, useEffect } from 'react';
import { AdProvider as SdkAdProvider } from '@yuuues/myadserver-sdk-react';
import { fetchAdsConfig } from '../services/apiService';

const AdContext = createContext({ adsEnabled: false, loading: true });

/**
 * AdProvider wrapper that:
 * 1. Fetches ad configuration from backend
 * 2. Checks user's no_ads flag
 * 3. Conditionally wraps children with SDK AdProvider
 */
export function AdProvider({ children, user }) {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadConfig = async () => {
      try {
        const adsConfig = await fetchAdsConfig();
        setConfig(adsConfig);
      } catch (error) {
        console.error('[AdContext] Failed to load ads config:', error);
      } finally {
        setLoading(false);
      }
    };
    loadConfig();
  }, []);

  // Don't show ads if:
  // - User has no_ads flag set
  // - Ad server not configured on backend (config.enabled = false)
  const adsEnabled = !user?.no_ads && config?.enabled;

  // While loading, render children without ads context
  if (loading) {
    return (
      <AdContext.Provider value={{ adsEnabled: false, loading: true }}>
        {children}
      </AdContext.Provider>
    );
  }

  // If ads disabled, just provide context with adsEnabled: false
  if (!adsEnabled) {
    return (
      <AdContext.Provider value={{ adsEnabled: false, loading: false }}>
        {children}
      </AdContext.Provider>
    );
  }

  // Ads enabled - wrap with SDK provider
  return (
    <SdkAdProvider
      config={{
        apiUrl: config.apiUrl,
        apiKey: config.apiKey,
        timeout: 10000 // 10 seconds - ad server can be slow
      }}
    >
      <AdContext.Provider value={{ adsEnabled: true, loading: false }}>
        {children}
      </AdContext.Provider>
    </SdkAdProvider>
  );
}

export function useAds() {
  const context = useContext(AdContext);
  if (!context) {
    throw new Error('useAds must be used within an AdProvider');
  }
  return context;
}

export default AdContext;
